import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { resolveGuidTargets } from '@viewer/lib/resolveGuidTargets'
import { getCategory } from '@viewer/lib/ifcItemData'
import { getModelsBbox } from '@viewer/section-box/sectionMath'
import { IDENTITY_ORIENTATION } from '@viewer/store/sectionSlice'
import {
  type Caixa,
  caixaComFolga,
  extensoesDe,
  intervaloDosCursores,
} from './bcfElementos'

/**
 * Os elementos ligados a um tópico BCF na cena: onde estão nos modelos
 * abertos, seleccioná-los, enquadrá-los e pôr-lhes uma caixa de corte à
 * volta. A parte pura (que elementos, que cor, que caixa) está em
 * `bcfElementos.ts`.
 */

export interface Alvo {
  modelId: string
  localId: number
}

export interface Resolucao {
  /** Onde o elemento está nos modelos abertos. Vazio quando não está. */
  alvos: Alvo[]
  classe: string | null
  nome: string | null
  /** O nome do ficheiro do modelo em que foi encontrado primeiro. */
  modelo: string | null
}

/** Quantos GlobalId se resolvem de cada vez. A secção mostra o que já
 *  está resolvido enquanto o resto continua. */
export const LOTE_DE_RESOLUCAO = 20

/**
 * Resolve um lote de GlobalId em todos os modelos carregados, como o
 * `applyComponentsOnly` do viewpoint: um GUID de uma zona ou de um
 * sistema expande-se para os membros com geometria.
 */
export async function resolverElementos(guids: readonly string[]): Promise<Map<string, Resolucao>> {
  const modelos = useAppStore.getState().models
  const out = new Map<string, Resolucao>()
  for (const guid of guids) {
    const r: Resolucao = { alvos: [], classe: null, nome: null, modelo: null }
    for (const m of modelos) {
      const ids = await resolveGuidTargets(m.id, [guid]).catch(() => [] as number[])
      if (ids.length === 0) continue
      for (const localId of ids) r.alvos.push({ modelId: m.id, localId })
      if (r.modelo === null) {
        r.modelo = m.fileName
        const dados = await lerClasseENome(m.id, ids[0])
        r.classe = dados.classe
        r.nome = dados.nome
      }
    }
    out.set(guid, r)
  }
  return out
}

async function lerClasseENome(
  modelId: string,
  localId: number,
): Promise<{ classe: string | null; nome: string | null }> {
  const model = viewerApi.getModel(modelId)
  if (!model) return { classe: null, nome: null }
  try {
    const dados = await model.getItemsData([localId], {
      attributesDefault: false,
      attributes: ['Name'],
      relationsDefault: { attributes: false, relations: false },
    })
    const item = (dados?.[0] ?? {}) as Record<string, unknown>
    const classe = getCategory(item).toUpperCase() || null
    const cru = (item.Name as { value?: unknown } | undefined)?.value
    return { classe, nome: typeof cru === 'string' && cru ? cru : null }
  } catch {
    return { classe: null, nome: null }
  }
}

/** Selecciona os alvos (substitui a selecção) e pinta o `select`, como
 *  o "ir para o conflito" da detecção de conflitos. */
export async function seleccionar(alvos: readonly Alvo[]): Promise<void> {
  const store = useAppStore.getState()
  if (alvos.length === 0) {
    store.clearSelection()
    return
  }
  store.applyBulkSelection([...alvos], 'replace')
  const h = viewerApi.getHighlighter()
  if (!h) return
  const mapa: Record<string, Set<number>> = {}
  for (const a of alvos) (mapa[a.modelId] ??= new Set<number>()).add(a.localId)
  try {
    await h.highlightByID('select', mapa, true, false)
  } catch (err) {
    console.debug('[bcf] seleccionar ignorado', err)
  }
}

export async function enquadrar(alvos: readonly Alvo[]): Promise<void> {
  await viewerApi.fitToItems([...alvos])
}

function caixaDe(b: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } }): Caixa {
  return { min: [b.min.x, b.min.y, b.min.z], max: [b.max.x, b.max.y, b.max.z] }
}

/**
 * Liga a caixa de corte à volta dos alvos, com folga, e enquadra a
 * câmara. Orientação identidade; os cursores podem alargar a caixa até à
 * dos modelos. Devolve false quando nenhum alvo tem geometria.
 */
export async function caixaDeCorteA(alvos: readonly Alvo[]): Promise<boolean> {
  if (alvos.length === 0) return false
  const box = await viewerApi.getItemsWorldBox([...alvos])
  if (!box) return false
  const caixa = caixaComFolga(caixaDe(box))
  const modelos = getModelsBbox()
  const store = useAppStore.getState()
  store.setSectionOrientation(IDENTITY_ORIENTATION)
  store.setSectionExtents(extensoesDe(caixa))
  store.setSectionExtentRange(
    extensoesDe(intervaloDosCursores(caixa, modelos ? caixaDe(modelos) : null)),
  )
  store.setSectionEnabled(true)
  await enquadrar(alvos)
  return true
}
